import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CalendarCheck, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Screening Programme",
  "Individual Therapy (in-school)",
  "Group Therapy (in-school)",
  "Teacher Training",
  "Parent Counselling",
  "Not sure yet — need guidance",
];
const AGE_GROUPS = ["Pre-school (2–4 yrs)", "Primary (5–10 yrs)", "Middle school (11–13 yrs)", "High school (14+ yrs)"];
const ROLES = ["Principal / Management", "Coordinator", "Teacher", "Parent", "Other"];
const TIME_SLOTS = ["09:00", "10:30", "12:00", "14:00", "15:30", "17:00"];

const EMPTY = { name: "", email: "", phone: "", service: "", ageGroup: "", role: "", date: "", time: "", notes: "" };

const inputCls =
  "w-full rounded-xl border border-pine-900/15 bg-cream px-4 py-3 text-sm text-pine-950 outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-stone-400 focus:border-pine-700 focus:ring-2 focus:ring-pine-700/15";

export const BookingModal = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setForm((f) => ({ ...EMPTY, name: f.name, email: f.email, phone: f.phone }));
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const step1Valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim() && form.service;
  const step2Valid = form.date && form.time;

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        age_group: form.ageGroup,
        role: form.role,
        date: form.date,
        time: form.time,
        notes: form.notes,
      });
      toast.success("Consult request received", {
        description: `We'll confirm your ${form.date} at ${form.time} slot within one business day.`,
      });
      setForm(EMPTY);
      onClose();
    } catch {
      toast.error("Something went wrong", { description: "Please try again or email spellingbeetherapy@gmail.com." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-3xl border-pine-900/10 bg-white p-0" data-testid="booking-modal">
        <DialogHeader className="border-b border-pine-900/10 px-8 pb-5 pt-7">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Step {step} of 2</p>
          <DialogTitle className="font-serif text-2xl font-semibold text-pine-950">
            {step === 1 ? "Tell us about you" : "Pick your slot"}
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            {step === 1 ? "Sameeksha reviews every request personally." : "We'll call to confirm before any visit."}
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6">
          {step === 1 ? (
            <div className="space-y-4">
              <input data-testid="booking-form-name" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input data-testid="booking-form-email" className={inputCls} type="email" placeholder="Email" value={form.email} onChange={set("email")} />
                <input data-testid="booking-form-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
              </div>
              <select data-testid="booking-form-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                <option value="">What are you interested in?…</option>
                {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <select data-testid="booking-form-age-group" className={inputCls} value={form.ageGroup} onChange={set("ageGroup")}>
                  <option value="">Student age group…</option>
                  {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <select data-testid="booking-form-role" className={inputCls} value={form.role} onChange={set("role")}>
                  <option value="">I am a…</option>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <button
                data-testid="booking-modal-next"
                disabled={!step1Valid}
                onClick={() => setStep(2)}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-pine-900 py-3.5 text-sm font-semibold text-cream transition-[background-color,opacity] duration-300 hover:bg-pine-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Continue <ArrowRight size={15} />
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <input data-testid="booking-form-date" className={inputCls} type="date" min={new Date().toISOString().split("T")[0]} value={form.date} onChange={set("date")} />
              <div data-testid="booking-form-time" className="grid grid-cols-3 gap-2">
                {TIME_SLOTS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, time: t }))}
                    className={`rounded-xl border py-2.5 font-mono text-xs transition-[background-color,border-color,color] duration-200 ${
                      form.time === t
                        ? "border-pine-900 bg-pine-900 text-cream"
                        : "border-pine-900/15 bg-cream text-pine-900 hover:border-pine-700"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <textarea data-testid="booking-form-notes" className={`${inputCls} min-h-[90px] resize-none`} placeholder="School name, number of students, or anything we should know (optional)" value={form.notes} onChange={set("notes")} />
              <div className="flex gap-3">
                <button
                  data-testid="booking-modal-back"
                  onClick={() => setStep(1)}
                  className="flex items-center gap-1.5 rounded-full border border-pine-900/20 px-6 py-3.5 text-sm font-semibold text-pine-900 transition-colors hover:bg-sand"
                >
                  <ArrowLeft size={15} /> Back
                </button>
                <button
                  data-testid="booking-modal-submit"
                  disabled={!step2Valid || submitting}
                  onClick={submit}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-clay py-3.5 text-sm font-semibold text-white transition-[background-color,opacity] duration-300 hover:bg-[#b96a05] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />}
                  {submitting ? "Booking…" : "Request consult"}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
